/**
 * LLM message formatter for TransformDatasetTool results.
 *
 * Uses TOON format for sample data to reduce token consumption while
 * maintaining LLM comprehension of the data structure and content.
 */

import { buildContextInjection } from './context-injection.ts';
import { roundForDisplay } from './round-for-display.ts';
import type { AutoStatsResult, ColumnStats } from '#plugin-chat/lib/data-pipeline/engine/auto-stats.ts';
import type { AggregationResult, GroupedAggregations } from '#plugin-chat/lib/data-pipeline/types.ts';

/** Options for formatting TransformDatasetTool result message */
export interface FormatMessageOptions {
  inputDataset: string;
  outputDataset?: string;
  totalMatched: number;
  autoStats?: AutoStatsResult;
  columnWarnings?: string[];
  /**
   * Global aggregation results (sum/avg/min/max/count/...) keyed by the
   * alias the caller specified. Rendered as an `AGGREGATIONS:` section so
   * the LLM receives the headline number it asked for — without this the
   * engine's computed aggregation would be silently dropped and the LLM
   * would have to infer the answer from the row-level DATA sample.
   */
  aggregations?: AggregationResult;
  /**
   * Grouped aggregation results from a groupBy clause. Rendered as a
   * `GROUPED AGGREGATIONS:` section showing one line per group with its
   * key columns and per-group aggregate values.
   */
  groupedAggregations?: GroupedAggregations;
  /** Optional follow-up directive to append to message */
  followUpDirective?: string;
  /** Full dataset for context injection (token-limited sampling applied automatically) */
  fullData?: Record<string, unknown>[];
  /** Original count before filtering (for filter context) */
  originalCount?: number;
  /** Description of filter applied (shown in context when data was filtered) */
  filterDescription?: string;
  /** Maximum tokens for data injection (default: 2000) */
  maxTokens?: number;
}

/**
 * Format a single column's statistics for the summary.
 *
 * For numeric columns with no real numbers (every value null/undefined/NaN),
 * emit an explicit "empty" variant instead of the normal min/max/mean line —
 * the previous behaviour rendered zeros that the LLM misread as measurements.
 */
function formatColumnStats(name: string, stats: ColumnStats): string {
  const nullsTail = stats.nullCount > 0 ? ` (${stats.nullCount} nulls)` : '';
  switch (stats.type) {
    case 'numeric':
      if (stats.count === 0 || stats.mean === null) {
        return `${name} (numeric, empty): 0 of ${stats.nullCount} non-null — no statistics available`;
      }
      return `${name} (numeric): min=${roundForDisplay(stats.min as number)}, max=${roundForDisplay(stats.max as number)}, mean=${roundForDisplay(stats.mean)}, median=${roundForDisplay(stats.median as number)}${nullsTail}`;
    case 'string':
      return `${name} (string): ${stats.distinctCount} distinct values${nullsTail}`;
    case 'date':
      return `${name} (date): ${stats.min || 'N/A'} to ${stats.max || 'N/A'}${nullsTail}`;
    case 'mixed':
      return `${name} (mixed): ${stats.count} values, ${stats.nullCount} nulls`;
  }
}

/**
 * Format the statistics section for all columns.
 */
export function formatStatsSection(autoStats: AutoStatsResult | undefined): string {
  if (!autoStats || Object.keys(autoStats.columns).length === 0) {
    return 'No column statistics available';
  }

  const lines = Object.entries(autoStats.columns).map(([name, stats]) => formatColumnStats(name, stats));

  return lines.join('\n');
}

/**
 * Format a value computed by the aggregation engine. `null` / `undefined`
 * renders as `"no data"` (an empty column or a series with no datapoints
 * in range); numbers render as-is; strings are quoted to disambiguate from
 * numeric aliases. Shared by the global and grouped aggregation renderers.
 */
function formatAggregationValue(value: number | string | null | undefined): string {
  if (value === null || value === undefined) {
    return 'no data';
  }
  if (typeof value === 'string') {
    return `"${value}"`;
  }
  return String(roundForDisplay(value));
}

/**
 * Format the `AGGREGATIONS:` section. Each spec alias becomes a single
 * line so the LLM sees the headline number it asked for rather than
 * having to infer it from the row-level DATA sample.
 */
function _formatAggregationsSection(aggregations: AggregationResult): string {
  return Object.entries(aggregations)
    .map(([name, value]) => `${name}: ${formatAggregationValue(value)}`)
    .join('\n');
}

/**
 * Format the `GROUPED AGGREGATIONS:` section. One line per group:
 * `<col=val, …> → <aggName>=<value>, …`. Falls back to the composite
 * key string when `groupKeys` doesn't carry the reconstructed column
 * map.
 */
function formatGroupKeyValue(value: unknown): string {
  if (value === null || value === undefined) {
    return 'no data';
  }
  if (typeof value === 'number') {
    return String(roundForDisplay(value));
  }
  return String(value);
}

function _formatGroupedAggregationsSection(grouped: GroupedAggregations): string {
  const keys = Object.keys(grouped.groups);
  if (keys.length === 0) {
    return 'No groups produced';
  }
  return keys
    .map((key) => {
      const groupCols = grouped.groupKeys[key];
      const label =
        groupCols && Object.keys(groupCols).length > 0
          ? Object.entries(groupCols)
              .map(([col, val]) => `${col}=${formatGroupKeyValue(val)}`)
              .join(', ')
          : key;
      const aggs = grouped.groups[key] ?? {};
      const aggParts = Object.entries(aggs).map(([name, value]) => `${name}=${formatAggregationValue(value)}`);
      return `${label} → ${aggParts.join(', ')}`;
    })
    .join('\n');
}

/**
 * Format TransformDatasetTool result as an LLM-friendly message.
 *
 * The message includes:
 * - Natural language dataset summary (when fullData provided)
 * - Summary of operation (rows processed, source/output keys)
 * - Column statistics (type-aware summary for each column)
 * - Token-limited data section (TOON-formatted via context injection)
 * - Any warnings (e.g., invalid column names)
 *
 * This format reduces token consumption by 30-60% compared to raw JSON
 * while maintaining LLM comprehension of the data.
 */
export function formatTransformDatasetMessage(options: FormatMessageOptions): string {
  const {
    inputDataset,
    outputDataset,
    totalMatched,
    autoStats,
    columnWarnings,
    aggregations,
    groupedAggregations,
    followUpDirective,
    fullData,
    originalCount,
    filterDescription,
    maxTokens = 5000,
  } = options;

  // Handle zero-row outcomes: distinguish genuinely-empty sources from
  // filters that dropped every input row. Same literal used to cover both,
  // which misled the LLM into concluding "source has no data" when the
  // filter was simply incorrect.
  if (totalMatched === 0) {
    if (originalCount !== undefined && originalCount > 0) {
      return `Filter matched 0 rows of ${originalCount} from "${inputDataset}". The source is not empty — the filter value is almost certainly wrong. Do NOT retry with another guessed value. Instead, re-run transformDataset with groupBy (no filter) on the filter column to discover the actual value encoding, then re-filter using a literal value copied from that groupBy output.`;
    }
    return `Source "${inputDataset}" is empty (0 rows).`;
  }

  // Build header
  const outputDesc = outputDataset ? `, stored as "${outputDataset}"` : '';
  const header = `Processed ${totalMatched} rows from "${inputDataset}"${outputDesc}`;

  // Build sections
  const sections: string[] = [header, ''];

  // Column statistics section
  sections.push('COLUMN STATS:');
  sections.push(formatStatsSection(autoStats));
  sections.push('');

  // // Aggregations section — placed BEFORE DATA so the LLM reads the
  // // headline number it asked for without first wading through row-level
  // // samples. Without this block the engine's computed aggregation would
  // // be dropped at the tool-message boundary and the LLM would have to
  // // hallucinate or infer the answer from the DATA sample.
  // if (aggregations && Object.keys(aggregations).length > 0) {
  //   sections.push('AGGREGATIONS:');
  //   sections.push(formatAggregationsSection(aggregations));
  //   sections.push('');
  // }
  //
  // if (groupedAggregations && Object.keys(groupedAggregations.groups).length > 0) {
  //   sections.push('GROUPED AGGREGATIONS:');
  //   sections.push(formatGroupedAggregationsSection(groupedAggregations));
  //   sections.push('');
  // }

  // Data section — token-limited context injection derived from the full dataset
  if (fullData && fullData.length > 0) {
    const contextInjection = buildContextInjection(fullData, {
      maxTokens,
      originalCount,
      filterDescription,
    });
    sections.push('DATA:');
    sections.push(contextInjection);
  }

  // Warnings section (if any)
  if (columnWarnings && columnWarnings.length > 0) {
    sections.push('');
    sections.push('WARNINGS:');
    sections.push(columnWarnings.join('\n'));
  }

  // Follow-up directive section (if provided)
  if (followUpDirective) {
    sections.push('');
    sections.push('---');
    sections.push(followUpDirective);
  }

  return sections.join('\n');
}
